import { ComponentType, createElement } from 'react';
import { createBrowserRouter } from 'react-router';
import { RequireAuth } from './auth/RequireAuth';
import { SignIn } from './pages/SignIn';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Options } from './pages/Options';
import { Dashboard } from './pages/Dashboard';
import { LiveGame } from './pages/LiveGame';
import { GameHistory } from './pages/GameHistory';
import { TeamManagement } from './pages/TeamManagement';
import { RockMap } from './pages/RockMap';
import { SheetMap } from './pages/SheetMap';
import { BRCScoring } from './pages/BRCScoring';
import { BRCScoringSheet } from './pages/BRCScoringSheet';
import { BRCScoringTeams } from './pages/BRCScoringTeams';
import { PracticeShots } from './pages/PracticeShots';
import { ClubSheetGames } from './pages/ClubSheetGames';
import { PlayerSummary } from './pages/PlayerSummary';
import { Profile } from './pages/Profile';
import { ProgramManagement } from './pages/ProgramManagement';
import { Admin } from './pages/Admin';
import { SponsorView } from './pages/SponsorView';
import { LiveGames } from './pages/LiveGames';
import { LiveGameDetail } from './pages/LiveGameDetail';

/** Wraps a page in the session guard. */
const guarded = (page: ComponentType) => () =>
  createElement(RequireAuth, null, createElement(page));

/** Session guard plus: redirects to /options until the user has an active account. */
const guardedActive = (page: ComponentType) => () =>
  createElement(RequireAuth, { requireActiveAccount: true }, createElement(page));

export const router = createBrowserRouter([
  // Unguarded: signing in, registering, and recovering a forgotten password.
  {
    path: '/',
    Component: SignIn,
  },
  {
    path: '/forgot-password',
    Component: ForgotPassword,
  },
  {
    path: '/reset-password',
    Component: ResetPassword,
  },
  // Not guarded: Options.tsx itself renders a stripped-down guest view (View Live
  // Games only) when nobody is signed in, reached via SignIn's Guest Access button.
  {
    path: '/options',
    Component: Options,
  },
  {
    path: '/CurlingCodex',
    Component: guardedActive(Dashboard),
  },
  {
    path: '/CurlingCodex/game/:gameId',
    Component: guardedActive(LiveGame),
  },
  {
    path: '/CurlingCodex/game/:gameId/rock-map',
    Component: guardedActive(RockMap),
  },
  {
    path: '/CurlingCodex/rock-map/:club/:sheet',
    Component: guardedActive(RockMap),
  },
  {
    path: '/CurlingCodex/club-sheet/:club/:sheet',
    Component: guardedActive(ClubSheetGames),
  },
  {
    path: '/CurlingCodex/game/:gameId/sheet-map',
    Component: guardedActive(SheetMap),
  },
  {
    path: '/CurlingCodex/game/:gameId/brc-scoring',
    Component: guardedActive(BRCScoring),
  },
  {
    path: '/CurlingCodex/game/:gameId/brc-scoring/sheet',
    Component: guardedActive(BRCScoringSheet),
  },
  {
    path: '/CurlingCodex/game/:gameId/brc-scoring/teams',
    Component: guardedActive(BRCScoringTeams),
  },
  {
    path: '/CurlingCodex/game/:gameId/player-summary',
    Component: guardedActive(PlayerSummary),
  },
  {
    path: '/CurlingCodex/game/:gameId/practice',
    Component: guardedActive(PracticeShots),
  },
  {
    path: '/CurlingCodex/history',
    Component: guardedActive(GameHistory),
  },
  {
    path: '/CurlingCodex/teams',
    Component: guardedActive(TeamManagement),
  },
  {
    path: '/profile',
    Component: guarded(Profile),
  },
  {
    path: '/program',
    Component: guardedActive(ProgramManagement),
  },
  {
    path: '/admin',
    Component: guarded(Admin),
  },
  {
    path: '/sponsor',
    Component: guarded(SponsorView),
  },
  // Public: the live-games spectator dashboard needs no session at all. Its
  // per-game detail is guarded, but only by plain sign-in (no active account
  // required) — a free registration is the whole bar, on purpose.
  {
    path: '/live-games',
    Component: LiveGames,
  },
  {
    path: '/live-games/:gameId',
    Component: guarded(LiveGameDetail),
  },
]);
